/*
 * Copyright (c) 2026 Joseph Duggan. All rights reserved.
 */

import { appPalette } from '@sidekick/design-tokens';
import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { monoLabel } from '../../lib/ui.js';

const p = appPalette;

/**
 * One past review, normalized across weekly/quarterly so a single
 * presentation handles both. `priorities` is the next-period list; its
 * heading varies by surface (`Next week` / `Next quarter`).
 */
export interface PastReview {
  id: string;
  periodEnd: string;
  closingObservation: string | null;
  reviewMarkdown: string;
  priorities: readonly string[];
}

/**
 * The shared "Past reviews" feed for the weekly and quarterly surfaces.
 * Each entry collapses to its period + closing observation; expanding it
 * reveals the full review markdown and the next-period priorities.
 */
export function ReviewHistory({
  label,
  prioritiesLabel,
  reviews,
  isPending,
}: {
  label: string;
  prioritiesLabel: string;
  reviews: PastReview[];
  isPending: boolean;
}) {
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={monoLabel}>{label}</div>

      {isPending ? (
        <span style={{ ...monoLabel, marginTop: 8 }}>…</span>
      ) : reviews.length === 0 ? (
        <p style={emptyLine}>No past reviews yet.</p>
      ) : (
        <div>
          {reviews.map((r) => (
            <PastReviewRow key={r.id} review={r} prioritiesLabel={prioritiesLabel} />
          ))}
        </div>
      )}
    </section>
  );
}

function PastReviewRow({
  review,
  prioritiesLabel,
}: {
  review: PastReview;
  prioritiesLabel: string;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: '14px 0',
        borderTop: `1px solid ${p.line}`,
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 12,
          background: 'transparent',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          textAlign: 'left',
          font: 'inherit',
          color: 'inherit',
        }}
      >
        <span style={{ ...monoLabel, color: p.fg }}>{formatDate(review.periodEnd)}</span>
        <span style={{ ...monoLabel, color: p.muted }}>{open ? '−' : '+'}</span>
      </button>

      {review.closingObservation ? (
        <p
          style={{
            fontFamily: 'var(--font-serif)',
            fontFeatureSettings: 'var(--serif-feat)',
            fontStyle: 'italic',
            fontSize: 15,
            color: p.muted,
            margin: 0,
          }}
        >
          {review.closingObservation}
        </p>
      ) : null}

      {open ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 4 }}>
          <div className="serif" style={{ fontSize: 15, lineHeight: 1.55, color: p.fg }}>
            <ReactMarkdown>{review.reviewMarkdown}</ReactMarkdown>
          </div>
          {review.priorities.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={monoLabel}>{prioritiesLabel}</span>
              <ol style={{ margin: 0, paddingLeft: 20 }}>
                {review.priorities.map((pr, i) => (
                  <li key={i} style={priorityItem}>
                    {pr}
                  </li>
                ))}
              </ol>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

const emptyLine: React.CSSProperties = {
  fontFamily: 'var(--font-serif)',
  fontFeatureSettings: 'var(--serif-feat)',
  fontStyle: 'italic',
  fontSize: 15,
  color: p.muted,
  margin: 0,
};

const priorityItem: React.CSSProperties = {
  fontFamily: 'var(--font-serif)',
  fontFeatureSettings: 'var(--serif-feat)',
  fontSize: 15,
  color: p.fg,
  marginBottom: 4,
};

/** Format a date string for display, matching the mono date treatment. */
function formatDate(value: string): string {
  const d = new Date(value.length === 10 ? `${value}T00:00:00` : value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}
